'use client';

import type { InboxPullRequest, ReviewStateValues } from '@/app/(home)/inbox/types';
import type { InboxV2GithubPullRequestSearchResult } from '@/data/pull-requests/use-inbox-v2-github-pr-search';
import { memo, useState } from 'react';

import type { Command, CommandPullRequest } from '../types';

import { getPullRequestCommandId, useRecentPullRequests } from '../recent-pull-requests';
import { COMMAND_GROUPS } from '../types';
import { useRegisterCommands } from '../use-commands';

import { groupPullRequests, mergeStackContexts } from '@/app/(home)/inbox/utils/pull-requests';
import { usePrNavigation } from '@/components/providers/pr-navigation';
import { useInboxV2CheckSummaries } from '@/data/pull-requests/use-inbox-v2-check-summaries';
import { useInboxV2GithubPullRequestSearch } from '@/data/pull-requests/use-inbox-v2-github-pr-search';
import {
  useInboxV2MergedPullRequests,
  useInboxV2OpenPullRequests,
} from '@/data/pull-requests/use-inbox-v2-pull-requests';
import { useInboxV2Snoozes } from '@/data/pull-requests/use-inbox-v2-snoozes';
import { useSelectedRepositories } from '@/lib/hooks/use-selected-repositories';
import { useAuth } from '@/providers/auth-provider';
import { buildPrPath } from '@/utils/app-paths';

const MAX_LOCAL_PR_COMMANDS = 100;
const MAX_RECENT_PR_COMMANDS = 20;
const MAX_REMOTE_PR_COMMANDS = 20;
// Opening the palette reuses inbox lists up to this old instead of fetching them on every open.
const PALETTE_INBOX_STALE_TIME_MS = 5 * 60_000;

const NO_REMOTE_RESULTS: readonly InboxV2GithubPullRequestSearchResult[] = [];

// The meaning the palette's PR icons had, where a dismissed review showed as changes requested.
const REVIEW_STATES: Partial<Record<ReviewStateValues, CommandPullRequest['reviewState']>> = {
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'changes-requested',
  DISMISSED: 'changes-requested',
  NEEDS_REVIEW: 'review-required',
  PENDING: 'review-required',
};

function getInboxPullRequestId(pr: InboxPullRequest) {
  return getPullRequestCommandId({
    owner: pr.repository.owner,
    repo: pr.repository.name,
    number: pr.number,
  });
}

function getUniquePullRequestsByIdentity(prs: InboxPullRequest[]) {
  const pullRequestsByIdentity = new Map<string, InboxPullRequest>();

  for (const pr of prs) {
    pullRequestsByIdentity.set(getInboxPullRequestId(pr), pr);
  }

  return Array.from(pullRequestsByIdentity.values());
}

function readInboxState(pr: InboxPullRequest): CommandPullRequest['state'] {
  if (pr.mergedAt) {
    return 'merged';
  }
  if (pr.state === 'closed') {
    return 'closed';
  }
  return pr.draft ? 'draft' : 'open';
}

function fromInboxPullRequest(pr: InboxPullRequest, needsReview: boolean): CommandPullRequest {
  return {
    owner: pr.repository.owner,
    repo: pr.repository.name,
    number: pr.number,
    title: pr.title,
    state: readInboxState(pr),
    reviewState: REVIEW_STATES[pr.reviewState] ?? null,
    authorLogin: pr.author || null,
    authorAvatarUrl: pr.avatarUrl || null,
    updatedAt: pr.updatedAt || null,
    needsReview,
  };
}

function readSearchResultState(
  pr: InboxV2GithubPullRequestSearchResult,
): CommandPullRequest['state'] {
  if (pr.mergedAt) {
    return 'merged';
  }
  if (pr.state === 'closed') {
    return 'closed';
  }
  return pr.draft ? 'draft' : 'open';
}

function fromSearchResult(pr: InboxV2GithubPullRequestSearchResult): CommandPullRequest {
  return {
    owner: pr.owner,
    repo: pr.repo,
    number: pr.number,
    title: pr.title,
    state: readSearchResultState(pr),
    reviewState: null,
    authorLogin: pr.author || null,
    authorAvatarUrl: pr.authorAvatarUrl ?? null,
    updatedAt: pr.updatedAt || null,
    needsReview: false,
  };
}

function getAuthorSearchKeywords(author: string | null | undefined) {
  const normalizedAuthor = author?.trim();
  if (!normalizedAuthor) {
    return [];
  }

  return [
    normalizedAuthor,
    `@${normalizedAuthor}`,
    `author:${normalizedAuthor}`,
    `involves:${normalizedAuthor}`,
  ];
}

function pullRequestCommand(
  pullRequest: CommandPullRequest,
  navigate: ReturnType<typeof usePrNavigation>['navigate'],
  foundForQuery = '',
): Command {
  const prPath = buildPrPath({
    owner: pullRequest.owner,
    repo: pullRequest.repo,
    number: pullRequest.number,
  }).toString();

  return {
    id: getPullRequestCommandId(pullRequest),
    label: pullRequest.title,
    action: () => {
      navigate(prPath, { source: 'command_palette' });
    },
    keywords: [
      `${pullRequest.owner}/${pullRequest.repo}`,
      `#${pullRequest.number}`,
      `${pullRequest.number}`,
      ...getAuthorSearchKeywords(pullRequest.authorLogin),
      ...(foundForQuery ? [foundForQuery] : []),
    ],
    showOn: (pathname: string) => pathname !== prPath,
    group: COMMAND_GROUPS.PULL_REQUESTS.name,
    tag: 'pr-search',
    pullRequest,
  };
}

type InboxV2PrSearchCommandsProps = {
  isPaletteOpen?: boolean;
  isSearchModeActive?: boolean;
  searchTerm?: string;
};

/**
 * Registers PR search commands from inbox V2 data, the pull requests opened recently and, in PR
 * search mode, GitHub search results.
 * Fetches on demand while the palette is open so a cold cache still populates results.
 * Open and merged are queried separately to match the inbox split; command search should still be
 * useful when open PRs are ready and merged history is still loading.
 */
export const InboxV2PrSearchCommands = memo((props: InboxV2PrSearchCommandsProps) => {
  const { isPaletteOpen = false, isSearchModeActive = false, searchTerm = '' } = props;
  const { navigate } = usePrNavigation();
  const { user } = useAuth();
  const username =
    typeof user?.user_metadata?.user_name === 'string' ? user.user_metadata.user_name : '';
  const { selectedRepositoryIds } = useSelectedRepositories();
  const hasSelectedRepositories = selectedRepositoryIds.length > 0;

  const inboxQueryOptions = { enabled: isPaletteOpen, staleTime: PALETTE_INBOX_STALE_TIME_MS };
  const { data: openData } = useInboxV2OpenPullRequests(inboxQueryOptions);
  const { data: mergedData } = useInboxV2MergedPullRequests(inboxQueryOptions);
  const { snoozes } = useInboxV2Snoozes({ enabled: isPaletteOpen });
  // Failing checks put the viewer's own PR in Needs you, as on the inbox.
  const { data: openChecks } = useInboxV2CheckSummaries('open', inboxQueryOptions);
  const openPRs = openData?.pullRequests;
  const mergedPRs = mergedData?.pullRequests;
  const cachePopulated = openPRs !== undefined || mergedPRs !== undefined;
  const recentPullRequests = useRecentPullRequests(user?.id, MAX_RECENT_PR_COMMANDS);
  const githubSearch = useInboxV2GithubPullRequestSearch({
    query: searchTerm,
    enabled: isSearchModeActive && hasSelectedRepositories,
  });
  // GitHub search belongs to PR search mode. Outside it the hook can still report the typed
  // query as pending or hold the mode's last results, and neither may re-register the list on
  // each keystroke.
  // An empty query in the mode lists the inbox only, not results a previous query left behind.
  const remoteResults =
    isSearchModeActive && searchTerm.trim() ? githubSearch.results : NO_REMOTE_RESULTS;
  // GitHub matches the body too, so a result's title often lacks the query, and the palette's
  // ranking would drop the row. The query the results were found for goes on their rows as a
  // keyword. It is the one they arrived with: they stay on screen while a newer query loads.
  const [foundResults, setFoundResults] = useState(() => ({
    results: githubSearch.results,
    query: githubSearch.debouncedSearch || searchTerm,
  }));
  if (foundResults.results !== githubSearch.results) {
    setFoundResults({
      results: githubSearch.results,
      query: githubSearch.debouncedSearch || searchTerm,
    });
  }
  const remoteResultsQuery = foundResults.query;
  const isRemoteSearchStalled = isSearchModeActive && githubSearch.isStalled;
  const isRemoteSearchPending =
    isSearchModeActive &&
    (githubSearch.isPending || githubSearch.isFetching) &&
    !githubSearch.isStalled;

  useRegisterCommands(() => {
    if (!isPaletteOpen) {
      return [];
    }

    // PR search covers the selected repositories only, so with none the mode says so instead of
    // loading forever.
    if (!hasSelectedRepositories) {
      return isSearchModeActive
        ? [
            {
              id: 'pr-search-empty-sentinel',
              label: 'Select repositories on the pull requests page to search them.',
              action: () => {},
              tag: 'pr-search-empty' as const,
            },
          ]
        : [];
    }

    const loadingSentinel =
      (!cachePopulated && isSearchModeActive) || isRemoteSearchStalled
        ? [
            {
              id: 'pr-search-loading-sentinel',
              label: 'Loading...',
              action: () => {},
              tag: 'pr-search-loading' as const,
            },
          ]
        : [];
    const pendingSentinel = isRemoteSearchPending
      ? [
          {
            id: 'pr-search-pending-sentinel',
            label: 'Loading...',
            action: () => {},
            tag: 'pr-search-pending' as const,
          },
        ]
      : [];

    if (!cachePopulated && isSearchModeActive && remoteResults.length === 0) {
      return loadingSentinel;
    }

    // The inbox's Needs you section leads, in the inbox's order. A snoozed PR leaves it but can
    // still be searched for.
    const inboxPRs = getUniquePullRequestsByIdentity([...(openPRs ?? []), ...(mergedPRs ?? [])]);
    const stackContexts = mergeStackContexts(
      openData?.stackContexts ?? [],
      mergedData?.stackContexts ?? [],
    );
    const needsReviewPRs = groupPullRequests(inboxPRs, username, {
      stackContexts,
      checks: openChecks ?? {},
      snoozes,
    }).needsYou;
    const needsReviewIds = new Set(needsReviewPRs.map(getInboxPullRequestId));
    const localPullRequests = [
      ...needsReviewPRs.map((pr) => fromInboxPullRequest(pr, true)),
      ...inboxPRs
        .filter((pr) => !needsReviewIds.has(getInboxPullRequestId(pr)))
        .map((pr) => fromInboxPullRequest(pr, false)),
    ].slice(0, MAX_LOCAL_PR_COMMANDS);
    const knownIds = new Set(localPullRequests.map(getPullRequestCommandId));
    const recentOnlyPullRequests = recentPullRequests.filter(
      (pr) => !knownIds.has(getPullRequestCommandId(pr)),
    );
    for (const pr of recentOnlyPullRequests) {
      knownIds.add(getPullRequestCommandId(pr));
    }
    const remoteOnlyPullRequests = remoteResults
      .filter((pr) => !knownIds.has(getPullRequestCommandId(pr)))
      .slice(0, MAX_REMOTE_PR_COMMANDS)
      .map(fromSearchResult);

    const pullRequestCommands = [
      ...[...localPullRequests, ...recentOnlyPullRequests].map((pullRequest) =>
        pullRequestCommand(pullRequest, navigate),
      ),
      ...remoteOnlyPullRequests.map((pullRequest) =>
        pullRequestCommand(pullRequest, navigate, remoteResultsQuery),
      ),
    ];

    if (pullRequestCommands.length === 0) {
      const emptySentinel =
        isSearchModeActive && loadingSentinel.length === 0 && pendingSentinel.length === 0
          ? [
              {
                id: 'pr-search-empty-sentinel',
                label: 'No pull requests found.',
                action: () => {},
                tag: 'pr-search-empty' as const,
              },
            ]
          : [];

      return [...loadingSentinel, ...pendingSentinel, ...emptySentinel];
    }

    return [...loadingSentinel, ...pendingSentinel, ...pullRequestCommands];
  }, [
    cachePopulated,
    hasSelectedRepositories,
    isPaletteOpen,
    isRemoteSearchPending,
    isRemoteSearchStalled,
    isSearchModeActive,
    mergedData?.stackContexts,
    mergedPRs,
    navigate,
    openChecks,
    openData?.stackContexts,
    openPRs,
    recentPullRequests,
    remoteResults,
    remoteResultsQuery,
    snoozes,
    username,
  ]);

  return null;
});
